import type { Deck } from '@/lib/api';
import { normalizeColorIdentity } from '@/lib/utils';
import { WUBRG_COLORS, WUBRG_ORDER, BRACKET_COLORS } from '@/lib/constants';
import type React from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type GroupMode = 'bracket' | 'color' | 'alpha' | 'recent' | 'curve';

export interface DeckGroup {
  header: string;
  decks: Deck[];
  badge?: React.ReactNode;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const COLOR_LABELS: Record<string, string> = {
  '': 'Colorless',
  'W': 'Mono-White', 'U': 'Mono-Blue', 'B': 'Mono-Black',
  'R': 'Mono-Red', 'G': 'Mono-Green',
  'WU': 'Azorius', 'WB': 'Orzhov', 'WR': 'Boros', 'WG': 'Selesnya',
  'UB': 'Dimir', 'UR': 'Izzet', 'UG': 'Simic',
  'BR': 'Rakdos', 'BG': 'Golgari', 'RG': 'Gruul',
  'WUB': 'Esper', 'WUR': 'Jeskai', 'WUG': 'Bant',
  'WBR': 'Mardu', 'WBG': 'Abzan', 'WRG': 'Naya',
  'UBR': 'Grixis', 'UBG': 'Sultai', 'URG': 'Temur', 'BRG': 'Jund',
  'WUBR': 'Non-Green', 'WUBG': 'Non-Red', 'WURG': 'Non-Black',
  'WBRG': 'Non-Blue', 'UBRG': 'Non-White',
  'WUBRG': 'Five Color',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalizeColors(deck: Deck): string {
  return normalizeColorIdentity(deck.color_identity)
    .sort((a, b) => WUBRG_ORDER.indexOf(a) - WUBRG_ORDER.indexOf(b))
    .join('');
}

/** Weighted average CMC from mana_curve data. Returns Infinity when unavailable (sorts last). */
export function getCurveSortKey(d: Deck): number {
  const mc = d.latest_analysis?.power_level?.mana_curve;
  if (!mc) return Infinity;
  let total = 0, weighted = 0;
  for (let i = 0; i <= 7; i++) {
    const count = mc[String(i)] ?? 0;
    total += count;
    weighted += i * count;
  }
  return total > 0 ? weighted / total : Infinity;
}

function getDeckBracket(d: Deck): number | null {
  // The shown bracket is the analysis's (the rule floor, or the owner's declaration) — never
  // the builder's target bracket.
  return d.latest_analysis?.power_level?.wotc_bracket ?? null;
}

// ─── Grouping Logic ──────────────────────────────────────────────────────────

export function groupDecks(decks: Deck[], mode: GroupMode): DeckGroup[] {
  if (mode === 'bracket') {
    const buckets: Record<string, Deck[]> = {};
    for (const deck of decks) {
      const bracket = getDeckBracket(deck);
      const key = bracket != null ? String(bracket) : 'Unrated';
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(deck);
    }
    const order = ['1', '2', '3', '4', '5', 'Unrated'];
    const groups: DeckGroup[] = [];
    for (const key of order) {
      if (!buckets[key]) continue;
      buckets[key].sort((a, b) => a.name.localeCompare(b.name));
      const isBracket = key !== 'Unrated';
      groups.push({
        header: isBracket ? `Bracket ${key}` : 'Unrated',
        decks: buckets[key],
        badge: isBracket ? (
          <span
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-on-surface text-base font-bold"
            style={{
              background: BRACKET_COLORS[Number(key)] ?? '#666',
            }}
          >
            {key}
          </span>
        ) : null,
      });
    }
    return groups;
  }

  if (mode === 'color') {
    const buckets: Record<string, Deck[]> = {};
    for (const deck of decks) {
      const key = normalizeColors(deck);
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(deck);
    }
    const keys = Object.keys(buckets).sort((a, b) => {
      if (a.length !== b.length) return a.length - b.length;
      for (let i = 0; i < a.length; i++) {
        const ai = WUBRG_ORDER.indexOf(a[i]);
        const bi = WUBRG_ORDER.indexOf(b[i]);
        if (ai !== bi) return ai - bi;
      }
      return 0;
    });
    return keys.map(key => {
      buckets[key].sort((a, b) => a.name.localeCompare(b.name));
      const label = COLOR_LABELS[key] || key;
      const colorArr = key ? key.split('') : [];
      return {
        header: label,
        decks: buckets[key],
        badge: (
          <span className="inline-flex gap-0.5">
            {colorArr.length > 0
              ? colorArr.map(c => (
                  <span
                    key={c}
                    className="w-4 h-4 rounded-full inline-block"
                    style={{
                      background: WUBRG_COLORS[c] || '#666',
                      border: c === 'B' ? '1px solid #555' : '1px solid rgba(255,255,255,0.3)',
                    }}
                  />
                ))
              : (
                  <span
                    className="w-4 h-4 rounded-full inline-block"
                    style={{
                      background: '#888',
                    }}
                  />
                )}
          </span>
        ),
      };
    });
  }

  if (mode === 'alpha') {
    const sorted = [...decks].sort((a, b) => a.name.localeCompare(b.name));
    // Group by first letter
    const buckets: Record<string, Deck[]> = {};
    for (const deck of sorted) {
      const letter = (deck.name[0] || '#').toUpperCase();
      const key = /[A-Z]/.test(letter) ? letter : '#';
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(deck);
    }
    const keys = Object.keys(buckets).sort();
    return keys.map(key => ({
      header: key,
      decks: buckets[key],
    }));
  }

  if (mode === 'recent') {
    const sorted = [...decks].sort((a, b) => {
      const da = a.updated_at || a.created_at || '';
      const db = b.updated_at || b.created_at || '';
      return db.localeCompare(da);
    });

    // Group by relative date
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const weekStart = todayStart - 6 * 86400000;

    const today: Deck[] = [];
    const thisWeek: Deck[] = [];
    const older: Deck[] = [];

    for (const deck of sorted) {
      const ts = new Date(deck.updated_at || deck.created_at || 0).getTime();
      if (ts >= todayStart) today.push(deck);
      else if (ts >= weekStart) thisWeek.push(deck);
      else older.push(deck);
    }

    const groups: DeckGroup[] = [];
    if (today.length > 0) groups.push({ header: 'Today', decks: today });
    if (thisWeek.length > 0) groups.push({ header: 'This Week', decks: thisWeek });
    if (older.length > 0) groups.push({ header: 'Older', decks: older });
    if (groups.length === 0) groups.push({ header: 'All Decks', decks: [] });
    return groups;
  }

  if (mode === 'curve') {
    const fast: Deck[] = [], mid: Deck[] = [], high: Deck[] = [], none: Deck[] = [];
    for (const deck of decks) {
      const key = getCurveSortKey(deck);
      if (key === Infinity) none.push(deck);
      else if (key < 2.5) fast.push(deck);
      else if (key < 3.5) mid.push(deck);
      else high.push(deck);
    }
    const sortByCurve = (a: Deck, b: Deck) => getCurveSortKey(a) - getCurveSortKey(b);
    fast.sort(sortByCurve);
    mid.sort(sortByCurve);
    high.sort(sortByCurve);
    none.sort((a, b) => a.name.localeCompare(b.name));
    const groups: DeckGroup[] = [];
    if (fast.length > 0) groups.push({ header: 'Fast (< 2.5)', decks: fast, badge: <span className="material-icons text-lg text-primary">flash_on</span> });
    if (mid.length > 0) groups.push({ header: 'Midrange (2.5–3.5)', decks: mid, badge: <span className="material-icons text-lg text-on-surface-variant">show_chart</span> });
    if (high.length > 0) groups.push({ header: 'High Curve (3.5+)', decks: high, badge: <span className="material-icons text-lg text-on-surface-variant">trending_up</span> });
    if (none.length > 0) groups.push({ header: 'No curve data', decks: none });
    return groups.length > 0 ? groups : [{ header: 'All Decks', decks: [] }];
  }

  return [{ header: 'All Decks', decks }];
}

/** Apply sort direction to a groups array: desc reverses group order and within-group deck order. */
export function applyGroupDir(groups: DeckGroup[], dir: 'asc' | 'desc'): DeckGroup[] {
  if (dir === 'asc') return groups;
  return [...groups].reverse().map(g => ({ ...g, decks: [...g.decks].reverse() }));
}
